import { Typography, Box } from '@mui/material'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import DialogConfirm from '../../../../../../dialog/DialogConfirm'
import InputField from '../../../../../../FormControl/input/InputField'
import SelectMultipleField from '../../../../../../FormControl/input/SelectMultipleField'
import SelectField from '../../../../../../FormControl/input/SelectField'
import ButtonStartIcon from '../../../../../../button/ButtonStartIcon'
import SectionCollapse from '../../../../../../section/SectionCollapse'
import IconTooltipButton from '../../../../../../button/IconTooltipButton'
import TextEllipsis from '../../../../../../text/TextEllipsis'

const FormRule = ({ show, onClose, formData = {}, onEditRule, onDeleteRule, isPreview = false }) => {
  const levels = useSelector(
    (state) => state?.analytics?.analyticsCurriculum?.datasets?.curriculum?.levels || []
  )
  if (!levels.find((level) => level._id === null)) {
    levels.unshift({ _id: null, name: 'Not assessed' })
  }
  const status = useSelector(
    (state) => state?.analytics?.analyticsCurriculum?.datasets?.data?.data?.status || []
  )
  const [form, setForm] = useState(formData)
  const [errors, setErrors] = useState({})

  const [conditionContainers, setConditionContainers] = useState(formData.conditions || [])

  const isAddEnabled =
    !!form.condition && Array.isArray(form.includesLevelId) && form.includesLevelId.length > 0

  const conditionOptions = [
    { value: 'every', label: 'Every LC' },
    { value: 'some', label: 'Some LCs' }
  ]

  const getConditionLabel = (value) => conditionOptions.find((c) => c.value === value)?.label || ''
  const getLevelsLabel = (ids = []) =>
    levels
      .filter((l) => ids.includes(l._id))
      .map((l) => l.name)
      .join(', ')

  const onAddConditionContainer = () => {
    const newCondition = {
      id: Date.now(),
      condition: form.condition,
      includesLevelId: form.includesLevelId
    }
    const newConditions = [...(form.conditions || []), newCondition]
    onChange('conditions', newConditions)
  }

  const onRemoveConditionContainer = (index) => {
    const newConditions = (form.conditions || []).filter((_, i) => i !== index)
    onChange('conditions', newConditions)
  }

  const onChangeCondition = (index, name, value) => {
    const newConditions = (form.conditions || []).map((c, i) => {
      if (i === index) {
        return { ...c, [name]: value }
      } else {
        return c
      }
    })
    onChange('conditions', newConditions)
  }

  const onChange = (name, value) => {
    onEditRule({ ...form, [name]: value })
    setForm((prev) => ({ ...prev, [name]: value }))
  }
  return (
    <SectionCollapse
      title={
        <TextEllipsis style={{ maxWidth: 300 }}>
          {`${form.description || 'New Rule'} (Conditions ${form.conditions?.length || 0})`}
        </TextEllipsis>
      }
      defaultExpanded
      actions={
        isPreview ? null : (
          <IconTooltipButton
            onClick={onDeleteRule}
            title='Delete rule'
            color='secondary'
            icon={['fas', 'trash-alt']}
          />
        )
      }
    >
      <div className='grid grid-cols-4 items-center gap-4 pb-4'>
        <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
          Description
        </Typography>
        <div className='col-span-3'>
          {isPreview ? (
            <Typography variant='inherit' className='font-bold' color='primary' component='p'>
              {form.description}
            </Typography>
          ) : (
            <InputField
              id='description'
              name='description'
              value={form.description}
              error={errors?.description}
              helperText={errors?.description}
              onChange={(e) => onChange('description', e.target.value)}
              fullWidth
            />
          )}
        </div>
        <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
          Status
        </Typography>
        <div className='col-span-3'>
          {isPreview ? (
            <Typography variant='inherit' className='font-bold' color='primary' component='p'>
              {status.find((s) => s._id === form.statusId)?.name || ''}
            </Typography>
          ) : (
            <SelectMultipleField
              id='status'
              name='status'
              value={form.statusId}
              error={errors?.statusId}
              helperText={errors?.statusId}
              onChange={(e) => onChange('statusId', e.target.value)}
              options={status.map((s) => ({ value: s._id, label: s.name }))}
              fullWidth
            />
          )}
        </div>
      </div>
      <Box className='px-4 py-4 bg-primary text-primary-foreground flex items-center justify-between'>
        <Typography variant='inherit' className='font-bold uppercase' component='p'>
          Conditions
        </Typography>
        {!isPreview && (
          <ButtonStartIcon
            onClick={onAddConditionContainer}
            variant='contained'
            startIcon={['fas', 'plus']}
            color={'secondary'}
          >
            Add Condition
          </ButtonStartIcon>
        )}
      </Box>
      <Box className='py-4'>
        {form.conditions?.map((c, index) => (
          <div className='col-span-full flex items-center justify-start gap-2  p-2' key={c._id || index}>
            <div
              className='flex items-center justify-center bg-primary text-primary-foreground rounded-full font-bold '
              style={{
                width: 25,
                height: 25
              }}
            >
              {index + 1}
            </div>
            <div className='max-w-80px'>
              {isPreview ? (
                <Typography variant='inherit' className='font-bold text-nowrap' align='right' component='p'>
                  {getConditionLabel(c.condition)}
                </Typography>
              ) : (
                <SelectField
                  id='condition'
                  name='condition'
                  value={c.condition}
                  onChange={(e) => onChangeCondition(index, 'condition', e.target.value)}
                  options={conditionOptions}
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className=' text-nowrap' component='p'>
              are at
            </Typography>
            <div className='min-w-160px max-w-160px'>
              {isPreview ? (
                <Typography variant='inherit' component='p'>
                  {getLevelsLabel(c.includesLevelId)}
                </Typography>
              ) : (
                <SelectMultipleField
                  id='includesLevelId'
                  name='includesLevelId'
                  value={c.includesLevelId}
                  onChange={(e) => onChangeCondition(index, 'includesLevelId', e.target.value)}
                  options={levels.map((s) => ({ value: s._id, label: s.name }))}
                  fullWidth
                />
              )}
            </div>
            {!isPreview && (
              <IconTooltipButton
                onClick={() => onRemoveConditionContainer(index)}
                title='Remove'
                icon={['fas', 'trash-alt']}
              />
            )}
          </div>
        ))}
      </Box>
    </SectionCollapse>
  )
}

export default FormRule
